import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, IconButton, Typography, Paper, Grid, 
  Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import { Schedule } from '../types/scheduleTypes';
import { fetchWithAuth } from '../utils/api';
import { 
  formatDate, 
  formatTime, 
  formatDateString, 
  isSameDay, 
  getCurrentDateString, 
  getLocalDateString
} from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import ScheduleFormModal from './ScheduleFormModal';
import styles from './MonthlyCalendarView.module.css';
import { SseEvent } from '../utils/sse';
import { Subscription } from 'rxjs';
import { API_BASE_URL } from '../config/constants';
import { UserRole } from '../types/auth';

interface MonthlyCalendarViewProps {
  userRole: UserRole;
  employeeId: number | null;
  onSwitchToWeekView: () => void;
}

const MonthlyCalendarView: React.FC<MonthlyCalendarViewProps> = ({
  userRole,
  employeeId,
  onSwitchToWeekView
}) => {
  // authコンテキストから権限チェック関数を取得
  const { canEditSchedules, canViewScheduleDetails } = useAuth();
  
  // 実際の権限を確認
  const canEdit = canEditSchedules();
  const canViewDetails = canViewScheduleDetails();
  
  // 状態管理
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthSchedules, setMonthSchedules] = useState<Schedule[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // AuthContextからsseEventsを取得
  const { sseEvents } = useAuth();
  
  // 月の日付を計算
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // 月の最初の日
    const firstDay = new Date(year, month, 1);
    // 月の最後の日
    const lastDay = new Date(year, month + 1, 0);
    
    // 月の最初の日の曜日 (0 = 日曜日)
    const firstDayOfWeek = firstDay.getDay();
    
    // カレンダーに表示する日付の配列
    const days: (Date | null)[] = [];
    
    // 前月の日を追加（空白セル）
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // 当月の日を追加
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    // 6週間分のカレンダーにするために必要なら空白を追加
    const totalCells = 42; // 6行 × 7列
    while (days.length < totalCells) {
      days.push(null);
    }
    
    return days;
  }, [currentDate]);

  // 月のスケジュールをグループ化
  const schedulesByDay = useMemo(() => {
    const grouped: { [dateString: string]: Schedule[] } = {};
    
    monthSchedules.forEach(schedule => {
      const scheduleDate = new Date(schedule.startTime);
      const dateString = scheduleDate.toISOString().split('T')[0];
      
      if (!grouped[dateString]) {
        grouped[dateString] = [];
      }
      grouped[dateString].push(schedule);
    });
    
    return grouped;
  }, [monthSchedules]);
  
  // 月表示のタイトル
  const monthTitle = useMemo(() => {
    return currentDate.toLocaleDateString('ja-JP', { 
      year: 'numeric', 
      month: 'long' 
    });
  }, [currentDate]);
  
  // 月のスケジュールを取得
  const fetchMonthSchedules = useCallback(async () => {
    if (!employeeId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // 月の最初の日
      const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      // 翌月の最初の日
      const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
      
      const startDate = firstDay.toISOString().split('T')[0];
      const endDate = lastDay.toISOString().split('T')[0];
      
      // 従業員IDを明示的に含めて、特定の従業員のスケジュールのみを取得
      const response = await fetchWithAuth(
        `${API_BASE_URL}/schedules?startDate=${startDate}&endDate=${endDate}&employeeId=${employeeId}`
      );
      
      if (!response.ok) {
        throw new Error('スケジュールの取得に失敗しました');
      }
      
      const data: Schedule[] = await response.json();
      console.log(`${monthTitle}の${employeeId}のスケジュール取得完了: ${data.length}件`);
      setMonthSchedules(data);
    } catch (error) {
      console.error('月間スケジュールの取得に失敗しました', error);
      setError('スケジュールを読み込めませんでした');
    } finally {
      setLoading(false);
    }
  }, [currentDate, employeeId, monthTitle]);
  
  // 月が変わった時にスケジュールを取得
  useEffect(() => {
    fetchMonthSchedules();
  }, [fetchMonthSchedules]);
  
  // 月操作のハンドラ
  const handlePreviousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };
  
  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };
  
  const handleCurrentMonth = () => {
    setCurrentDate(new Date());
  };
  
  // 日付クリック時の処理
  const handleDateClick = (date: Date | null) => {
    // 編集権限がなければ何もしない
    if (!date || !canEdit) return;
    
    setSelectedDate(date);
  };
  
  // スケジュールクリック時の処理
  const handleScheduleClick = (e: React.MouseEvent, schedule: Schedule) => {
    e.stopPropagation();
    
    // 詳細表示権限がなければ何もしない
    if (!canViewDetails) return;
    
    setSelectedSchedule(schedule);
  };
  
  // 日付のスタイルを決定
  const getDateCellClass = (date: Date | null) => {
    if (!date) return styles.emptyCell;
    
    const today = new Date();
    const isToday = 
      date.getDate() === today.getDate() && 
      date.getMonth() === today.getMonth() && 
      date.getFullYear() === today.getFullYear();
      
    if (isToday) return `${styles.dateCell} ${styles.today}`;
    return styles.dateCell;
  };
  
  // スケジュール更新後の処理
  const handleScheduleModified = () => {
    fetchMonthSchedules();
  };

  // SSEイベントの購読 - 独自に接続せず、AuthContextから提供されるイベントを購読
  useEffect(() => {
    let subscription: Subscription | null = null;
    
    // sseEventsが存在し、管理者かemployeeIdがある場合のみ購読
    if (sseEvents && (userRole === 'admin' || employeeId)) {
      // イベントを購読
      subscription = sseEvents.subscribe({
        next: (event: SseEvent) => {
          // スケジュール関連のイベントを処理
          if (
            event.type === 'schedule_created' || 
            event.type === 'schedule_updated' || 
            event.type === 'schedule_deleted' ||
            // バックエンドの命名規則が異なる可能性があるため代替イベントタイプも確認
            event.type === 'schedule-created' ||
            event.type === 'schedule-updated' ||
            event.type === 'schedule-deleted'
          ) {
            console.log('MonthlyCalendar: イベント受信:', event.type, event.data);
            
            // 管理者はすべての更新を受け取る
            // 従業員は自分のスケジュールの更新のみ受け取る
            if (
              userRole === 'admin' || 
              (event.data?.employeeId === employeeId)
            ) {
              // スケジュールデータを再取得
              fetchMonthSchedules();
            }
          }
        },
        error: (error) => {
          console.error('SSEエラー:', error);
          setError('リアルタイム更新に問題が発生しました。ページを再読み込みしてください。');
        }
      });
      
      console.log('MonthlyCalendarView: SSEイベント購読を開始しました');
    }
    
    // クリーンアップ関数
    return () => {
      if (subscription) {
        subscription.unsubscribe();
        console.log('MonthlyCalendarView: SSEイベント購読を解除しました');
      }
    };
  }, [userRole, employeeId, fetchMonthSchedules, sseEvents]);

  // レスポンシブデザインのためのメディアクエリ
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  
  // モバイル表示用に予定の表示数を調整
  const getDisplayScheduleCount = useCallback(() => {
    if (isSmallMobile) return 2;
    if (isMobile) return 3;
    return 3; // デフォルト
  }, [isMobile, isSmallMobile]);
  
  return (
    <Box className={styles.calendarContainer}>
      {/* ヘッダー（月の切り替えなど） - 移動ボタンと月表示を左側に配置するよう修正 */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center', 
        mb: isMobile ? 1 : 2,
        mt: isMobile ? 1 : 2,
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        {/* 左側の月表示・操作エリア */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          flex: 2,
          order: isMobile ? 1 : 0,
          my: isMobile ? 1 : 0,
          width: isMobile ? '100%' : 'auto'
        }}>
          <IconButton 
            onClick={handlePreviousMonth} 
            color="primary" 
            size={isMobile ? "small" : "medium"}
            sx={{ mx: 0 }}
          >
            <ArrowBackIosNewIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ mx: 1, minWidth: isMobile ? 'auto' : '160px', textAlign: 'center' }}
          >
            {monthTitle}
          </Typography>
          
          <IconButton 
            onClick={handleNextMonth} 
            color="primary" 
            size={isMobile ? "small" : "medium"}
            sx={{ mx: 0 }}
          >
            <ArrowForwardIosIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>
        
        {/* 中央のスペース（空白） */}
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        
        {/* 右側のコントロールボタンエリア */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          flex: 2,
          order: isMobile ? 0 : 2
        }}>
          {!isSmallMobile && (
            <Button 
              variant="outlined" 
              startIcon={<TodayIcon />} 
              onClick={handleCurrentMonth}
              size="small"
              sx={{ mr: 1, fontSize: isMobile ? '0.7rem' : '0.8rem' }}
            >
              今月
            </Button>
          )}
          
          <Button
            variant="contained"
            startIcon={<ViewWeekIcon />}
            onClick={onSwitchToWeekView}
            size="small"
            sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
          >
            {isMobile ? '週表示' : '週表示に戻る'}
          </Button>
        </Box>
        
        {/* 超小型デバイス用の今月ボタン（下部に配置） */}
        {isSmallMobile && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button 
              variant="outlined" 
              startIcon={<TodayIcon />} 
              onClick={handleCurrentMonth}
              size="small"
              sx={{ fontSize: '0.7rem' }}
            >
              今月
            </Button>
          </Box>
        )}
      </Box>
      
      {/* カレンダーグリッド */}
      <Paper 
        elevation={isMobile ? 1 : 3} 
        className={styles.calendar}
      >
        {/* 曜日ヘッダー */}
        <Grid container className={styles.weekdayHeader}>
          {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
            <Grid item xs={12/7} key={day} className={
              index === 0 ? styles.sunday : (index === 6 ? styles.saturday : '')
            }>
              {day}
            </Grid>
          ))}
        </Grid>
        
        {/* カレンダー日付グリッド */}
        <Grid container className={styles.datesGrid}>
          {calendarDays.map((date, index) => {
            // その日のスケジュール
            const dateString = date ? date.toISOString().split('T')[0] : '';
            const daySchedules = schedulesByDay[dateString] || [];
            
            // デバイスサイズに応じて表示する予定数を調整
            const displayCount = getDisplayScheduleCount();
            const displaySchedules = daySchedules.slice(0, displayCount);
            const hasMoreSchedules = daySchedules.length > displayCount;
            
            return (
              <Grid 
                item 
                xs={12/7} 
                key={index} 
                className={getDateCellClass(date)}
                onClick={() => handleDateClick(date)}
              >
                {/* 日付表示 - バッジを削除して日付のみ表示 */}
                <Box className={styles.dateNumber}>
                  {date ? (
                    <Typography 
                      variant={isMobile ? "caption" : "body2"}
                      className={
                        new Date().toDateString() === date.toDateString()
                          ? styles.todayDate
                          : ''
                      }
                    >
                      {date.getDate()}
                    </Typography>
                  ) : null}
                </Box>
                
                {/* スケジュール表示 */}
                {date && (
                  <Box className={styles.eventsList}>
                    {displaySchedules.map(schedule => (
                      <Tooltip 
                        key={schedule.id} 
                        title={canViewDetails 
                          ? `${schedule.title || schedule.task} (${formatTime(schedule.startTime)}～${formatTime(schedule.endTime)})` 
                          : '予約済み'}
                        enterTouchDelay={50}
                        leaveTouchDelay={1500}
                      >
                        <Box 
                          className={styles.eventItem}
                          onClick={(e) => handleScheduleClick(e, schedule)}
                        >
                          <Typography noWrap variant="caption" sx={{ fontSize: isMobile ? '8px' : '10px' }}>
                            {canViewDetails 
                              ? `${isMobile ? '' : formatTime(schedule.startTime)} ${schedule.title || schedule.task}`
                              : '予約済み'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                    
                    {/* 表示しきれないスケジュールがある場合 */}
                    {hasMoreSchedules && (
                      <Typography 
                        variant="caption" 
                        className={styles.moreEvents}
                      >
                        他 {daySchedules.length - displayCount} 件
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Paper>
      
      {/* ローディングとエラー表示 */}
      {loading && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography>読み込み中...</Typography>
        </Box>
      )}
      
      {error && (
        <Box sx={{ textAlign: 'center', mt: 2, color: 'error.main' }}>
          <Typography>{error}</Typography>
        </Box>
      )}
      
      {/* 新規予定作成モーダル - 編集権限がある場合のみ表示 */}
      {selectedDate && canEdit && (
        <ScheduleFormModal
          userRole={userRole}
          employeeId={employeeId || 0}
          date={getLocalDateString(selectedDate)}
          onClose={() => setSelectedDate(null)}
          onScheduleAdded={handleScheduleModified}
        />
      )}
      
      {/* 予定編集モーダル */}
      {selectedSchedule && (
        <ScheduleFormModal
          userRole={userRole}
          initialData={selectedSchedule}
          onClose={() => setSelectedSchedule(null)}
          onScheduleUpdated={handleScheduleModified}
          onScheduleDeleted={handleScheduleModified}
        />
      )}
    </Box>
  );
};

export default MonthlyCalendarView;